import { Task, TASK_STATUS } from './task'
import { CalendarEvent, CalendarMode, EVENT_STATUS } from './calendar'

export enum SCHEDULE_TASKS_FILTER_VALUES {
	OVERDUE = 'OVERDUE',
	UPCOMING_TODAY = 'UPCOMING_TODAY',
	DONE_TODAY = 'DONE_TODAY',
}

export type ScheduleTasks = Array<Task>

export type ScheduleEvents = Array<CalendarEvent>

export interface TasksState {
	tasks: ScheduleTasks
	filter: SCHEDULE_TASKS_FILTER_VALUES
	selectedTasks: Array<string>
	taskTypes: Array<string>
	taskPriorities: Array<string>
	taskStatuses: Array<string>
	taskAccounts: Array<string>
	taskSearch: string
}

export interface EventsState {
	events: ScheduleEvents
	time: number
	loadingEvents: boolean
	tableFilters: ScheduleEventsTableFilters
	searchValue: string
	calendarMode: CalendarMode
}

export type Schedule = {
	events: ScheduleEvents
	tasks: ScheduleTasks
}

export interface ScheduleCoachNameData {
	_id: string
	name: string
}

export interface ScheduleEventsTableModel {
	id: string
	title: string
	owner: string
	date: number
	invites: string
	category: string
	status: EVENT_STATUS
}

export type ScheduleEventsTableFilterKeys = keyof Pick<
	ScheduleEventsTableModel,
	'category' | 'status'
>

export type ScheduleEventsTableFilters = Record<
	ScheduleEventsTableFilterKeys,
	Array<string>
>

export enum SCHEDULE_ACTION_TYPES {
	SCHEDULE_GET = 'SCHEDULE_GET',

	SCHEDULE_TASKS_GET = 'SCHEDULE_TASKS_GET',
	SCHEDULE_TASKS_SET_FILTER = 'SCHEDULE_TASKS_SET_FILTER',
	SCHEDULE_TASKS_SET_DONE_STATUS_FOR_TASK = 'SCHEDULE_TASKS_SET_DONE_STATUS_FOR_TASK',
	SCHEDULE_TASKS_DELETE_TASK = 'SCHEDULE_TASKS_DELETE_TASK',
	SCHEDULE_TASKS_CREATE_TASK = 'SCHEDULE_TASK_CREATE_TASK',

	SCHEDULE_EVENTS_GET = 'SCHEDULE_EVENTS_GET',
	SCHEDULE_EVENTS_GET_LOADING = 'SCHEDULE_EVENTS_GET_LOADING',
	SCHEDULE_EVENTS_SET_CALENDAR_TIME = 'SCHEDULE_EVENTS_SET_CALENDAR_TIME',
	SCHEDULE_EVENTS_DELETE_EVENT = 'SCHEDULE_EVENTS_DELETE_EVENT',
	SCHEDULE_EVENTS_TABLE_SET_FILTER = 'SCHEDULE_EVENTS_TABLE_SET_FILTER',
	SCHEDULE_EVENTS_TABLE_UNSET_FILTER = 'SCHEDULE_EVENTS_TABLE_UNSET_FILTER',
	SCHEDULE_EVENTS_SET_SEARCH_VALUE = 'SCHEDULE_EVENTS_SET_SEARCH_VALUE',
	SCHEDULE_EVENTS_RESET = 'SCHEDULE_EVENTS_RESET',
	SCHEDULE_EVENTS_CHANGE_MODE = 'SCHEDULE_EVENTS_CHANGE_MODE',

	SCHEDULE_TASK_SELECT = 'SCHEDULE_TASK_SELECT',
	SCHEDULE_TASK_UNSELECT = 'SCHEDULE_TASK_UNSELECT',
	SCHEDULE_TASK_SELECT_ALL = 'SCHEDULE_TASK_SELECT_ALL',
	SCHEDULE_TASK_UNSELECT_ALL = 'SCHEDULE_TASK_UNSELECT_ALL',

	SET_TASK_TYPES = 'SET_TASK_TYPES',
	SET_TASK_PRIORITIES = 'SET_TASK_PRIORITIES',
	SET_TASK_STATUSES = 'SET_TASK_STATUSES',
	SET_TASK_ACCOUNTS = 'SET_TASK_ACCOUNTS',
	SET_TASK_SEARCH = 'SET_TASK_SEARCH',
}

export interface ScheduleGetAction {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_GET
}

/**
 * Schedule calendar monthly action types
 */

export interface ScheduleGetEvents {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_EVENTS_GET
	payload: ScheduleEvents
}

export interface ScheduleGetEventsLoading {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_EVENTS_GET_LOADING
	payload: boolean
}

export interface ScheduleEventsSetCalendarDay {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_EVENTS_SET_CALENDAR_TIME
	payload: number
}

export interface ScheduleEventsDeleteEvent {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_EVENTS_DELETE_EVENT
	payload: string
}

export interface ScheduleEventsTableSetFilterValue {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_EVENTS_TABLE_SET_FILTER
	payload: {
		key: ScheduleEventsTableFilterKeys
		value: string
	}
}

export interface ScheduleEventsTableUnsetFilterValue {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_EVENTS_TABLE_UNSET_FILTER
	payload: {
		key: ScheduleEventsTableFilterKeys
		value: string
	}
}

export interface ScheduleEventsSetSearchValue {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_EVENTS_SET_SEARCH_VALUE
	payload: string
}

export interface ScheduleEventsReset {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_EVENTS_RESET
}

export interface ScheduleEventsChangeMode {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_EVENTS_CHANGE_MODE
	payload: CalendarMode
}

/**
 * Schedule task action types
 */

export interface ScheduleSetTaskAction {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_TASKS_GET
	payload: ScheduleTasks
}

export interface ScheduleTasksSetFilterAction {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_TASKS_SET_FILTER
	payload: SCHEDULE_TASKS_FILTER_VALUES
}

export interface ScheduleTasksChangeStatusOfTaskAction {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_TASKS_SET_DONE_STATUS_FOR_TASK
	payload: {
		id: string
		status: TASK_STATUS
	}
}

export interface ScheduleTasksCreateAction {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_TASKS_CREATE_TASK
	payload: Task
}

export interface ScheduleTasksDeleteTaskAction {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_TASKS_DELETE_TASK
	payload: string
}

export interface ScheduleTaskSelectAction {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_TASK_SELECT
	payload: string
}

export interface ScheduleTaskUnselectAction {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_TASK_UNSELECT
	payload: string
}

export interface ScheduleSetTaskTypesAction {
	type: SCHEDULE_ACTION_TYPES.SET_TASK_TYPES
	payload: string[]
}

export interface ScheduleSetTaskPrioritiesAction {
	type: SCHEDULE_ACTION_TYPES.SET_TASK_PRIORITIES
	payload: string[]
}

export interface ScheduleSetTaskStatusesAction {
	type: SCHEDULE_ACTION_TYPES.SET_TASK_STATUSES
	payload: string[]
}

export interface ScheduleSetTaskAccountsAction {
	type: SCHEDULE_ACTION_TYPES.SET_TASK_ACCOUNTS
	payload: string[]
}
export interface ScheduleSetTaskSearchAction {
	type: SCHEDULE_ACTION_TYPES.SET_TASK_SEARCH
	payload: string
}

export interface ScheduleTaskSelectAllAction {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_TASK_SELECT_ALL
}

export interface ScheduleTaskUnselectAllAction {
	type: SCHEDULE_ACTION_TYPES.SCHEDULE_TASK_UNSELECT_ALL
}

/**
 * Schedule action types
 */

export type ScheduleAction =
	| ScheduleGetAction
	| ScheduleSetTaskAction
	| ScheduleGetEvents
	| ScheduleTasksSetFilterAction
	| ScheduleTasksChangeStatusOfTaskAction
	| ScheduleTasksDeleteTaskAction
	| ScheduleGetEventsLoading
	| ScheduleEventsSetCalendarDay
	| ScheduleEventsDeleteEvent
	| ScheduleTasksCreateAction
	| ScheduleTaskSelectAction
	| ScheduleTaskSelectAllAction
	| ScheduleTaskUnselectAllAction
	| ScheduleTaskUnselectAction
	| ScheduleSetTaskTypesAction
	| ScheduleSetTaskPrioritiesAction
	| ScheduleSetTaskStatusesAction
	| ScheduleSetTaskAccountsAction
	| ScheduleSetTaskSearchAction
	| ScheduleEventsTableSetFilterValue

export type ScheduleEventsAction =
	| ScheduleGetEvents
	| ScheduleGetEventsLoading
	| ScheduleEventsSetCalendarDay
	| ScheduleEventsDeleteEvent
	| ScheduleEventsTableSetFilterValue
	| ScheduleEventsTableUnsetFilterValue
	| ScheduleEventsReset
	| ScheduleEventsSetSearchValue
	| ScheduleEventsChangeMode
